/**
 * ShareModal Component
 * 
 * Modal for sharing game completion results via email.
 * 
 * @module ShareModal
 */

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { TIMEOUTS } from '../../constants/timing';
import { APP_MESSAGES } from '../../constants/messages';

/**
 * ShareModal - Email sharing modal for game completion
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Function to close modal
 * @param {string} props.subjectName - Display name of subject completed
 * @param {number} props.score - Final score (points)
 * @param {number} props.percentage - Score percentage (0-100)
 * @param {number} props.questionsAnswered - Number of questions answered
 * @param {string} props.shareEmail - Email address for sharing
 * @param {Function} props.setShareEmail - Setter for share email
 * @param {string} props.shareMessage - Personal message for sharing
 * @param {Function} props.setShareMessage - Setter for share message
 * @param {Function} props.onShare - Function to handle email sharing (service parameter)
 * @param {boolean} props.isSendingEmail - Whether email is currently being sent
 * @param {boolean} props.emailSent - Whether email was successfully sent
 * 
 * @returns {JSX.Element} ShareModal component
 */
const ShareModal = memo(({
  isOpen,
  onClose,
  subjectName,
  score,
  percentage,
  questionsAnswered,
  shareEmail,
  setShareEmail,
  shareMessage,
  setShareMessage,
  onShare,
  isSendingEmail,
  emailSent
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl p-8 max-w-md mx-4 shadow-2xl">
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">📧</div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Share Your Success!</h3>
          <p className="text-gray-600">Let your parents know about your amazing progress!</p>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Parent's Email</label>
            <input
              type="email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              placeholder="mom@example.com or dad@example.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Personal Message (Optional)</label>
            <textarea
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              placeholder="Add a personal message..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">📊 Your Results:</h4>
            <div className="text-sm text-blue-700 space-y-1">
              <p>• Subject: {subjectName}</p>
              <p>• Score: {score} points</p>
              <p>• Accuracy: {percentage}%</p>
              <p>• Questions: {questionsAnswered}</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-3 mt-6">
          <div className="text-center">
            <button
              onClick={() => onShare('direct')}
              disabled={!shareEmail || isSendingEmail}
              className="px-8 py-4 bg-green-500 text-white rounded-full font-bold text-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg transform hover:scale-105 active:scale-95"
            >
              {isSendingEmail ? '⏳ Sending...' : '📧 Send Now'}
            </button>
          </div>
          
          {emailSent && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg text-center">
              ✅ Email sent successfully!
            </div>
          )}
          
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

ShareModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  subjectName: PropTypes.string.isRequired,
  score: PropTypes.number.isRequired,
  percentage: PropTypes.number.isRequired,
  questionsAnswered: PropTypes.number.isRequired,
  shareEmail: PropTypes.string.isRequired,
  setShareEmail: PropTypes.func.isRequired,
  shareMessage: PropTypes.string.isRequired,
  setShareMessage: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  isSendingEmail: PropTypes.bool.isRequired,
  emailSent: PropTypes.bool.isRequired
};

ShareModal.displayName = 'ShareModal';

export default ShareModal;

